import React, {useMemo, useState} from 'react'
import {generatePath} from 'react-router'
import {BoxLink} from '../../common/router_links/BoxLink'
import {BoxLite} from '../../common/types/box'
import {Routes} from '../../common/Routes'
import {toDisplayDateTime} from '../../common/utils/dateUtils'
import {resolveBoxDisplayName} from '../../common/utils/resolveBoxDisplayName'
import {Box} from '../../webui/Box'
import {Paper} from '../../webui/Paper'
import {SortablePaginatedTable} from '../../webui/table/sortable-paginated-table/SortablePaginatedTable'
import {TableHeadCell} from '../../webui/table/sortable-paginated-table/TableHeadCell'
import {CONTRACT_TYPES} from '../../common/domain/contractType'
import {GQL_ContractType} from '../../generated/graphql'
import {Button} from '../../webui/Button'
import {BoxesPageFilter, BoxesPageFilterState} from './BoxesPageFilter'

interface BoxesTableProps {
  boxes: BoxLite[]
  filter: BoxesPageFilterState
  setFilter: (next: BoxesPageFilterState) => void
  onClearFilter: () => void
}

export interface BoxData {
  id: string
  name: string
  compartments: number
  organisation: string
  contractType: GQL_ContractType | null
  active: boolean
  temporarilyClosed: boolean
  latestRental: string

  // Extra columns
  serialNumber: string
  deprecated: boolean
  sponsor: string
  circuitBoardId: string
  initialActivationTime: string

  priceWeight: any
}

const EXTRA_COLUMN_IDS: Array<Extract<keyof BoxData, string>> = ['serialNumber', 'deprecated', 'sponsor', 'circuitBoardId', 'initialActivationTime']

const boxHeadCells: TableHeadCell<BoxData>[] = [
  {
    id: 'name',
    label: 'Name',
    maxWidth: 240,
    render: (row) => <BoxLink boxId={row.id} linkText={row.name} />,
  },
  {
    id: 'compartments',
    label: 'Comp.',
    maxWidth: 65,
    render: (row) => row.compartments,
  },
  {
    id: 'organisation',
    label: 'Organisation',
    maxWidth: 220,
    render: (row) => row.organisation,
  },
  {
    id: 'contractType',
    label: 'Contract type',
    maxWidth: 140,
    render: (row) => (row.contractType ? CONTRACT_TYPES[row.contractType].label : 'Not set'),
  },
  {
    id: 'active',
    label: 'Active',
    render: (row) => (row.active ? 'yes' : 'no'),
  },
  {
    id: 'temporarilyClosed',
    label: 'Temp. closed',
    maxWidth: 120,
    render: (row) => (row.temporarilyClosed ? 'yes' : 'no'),
  },
  {
    id: 'latestRental',
    label: 'Latest rental',
    maxWidth: 120,
    render: (row) => toDisplayDateTime(row.latestRental),
  },

  // Extra columns (right-most)
  {
    id: 'serialNumber',
    label: 'Serial number',
    isExtra: true,
    render: (row) => row.serialNumber || '—',
  },
  {
    id: 'circuitBoardId',
    label: 'Circuit board id',
    isExtra: true,
    render: (row) => row.circuitBoardId,
  },
  {
    id: 'initialActivationTime',
    label: 'Initial activation time',
    maxWidth: 120,
    isExtra: true,
    render: (row) => toDisplayDateTime(row.initialActivationTime),
  },
  {
    id: 'sponsor',
    label: 'Sponsor',
    maxWidth: 120,
    isExtra: true,
    render: (row) => row.sponsor,
  },
  {
    id: 'deprecated',
    label: 'Deprecated',
    maxWidth: 60,
    isExtra: true,
    render: (row) => (row.deprecated ? 'yes' : ''),
  },
]

const createRows = (boxes: BoxLite[]): BoxData[] =>
  boxes.map((box) => {
    const ongoingRentals = box.compartments.map((c) => c.ongoingRental).filter((r): r is NonNullable<typeof r> => !!r)
    const latestOngoing = ongoingRentals.length > 0 ? ongoingRentals.reduce((a, b) => ((a.rentStart ?? '') > (b.rentStart ?? '') ? a : b)) : undefined

    return {
      id: box.id,
      name: resolveBoxDisplayName(box),
      compartments: box.compartments.length,
      organisation: (box as any).organisationName ?? '',
      contractType: ((box as any).contractType as GQL_ContractType | null) ?? null,
      active: box.active,
      temporarilyClosed: box.temporarilyClosed,
      latestRental: latestOngoing?.rentStart ?? '',

      // Extra columns
      serialNumber: box.serialNumber ?? '',
      circuitBoardId: box.circuitBoardId,
      initialActivationTime: box.initialActivationTime ?? '',
      sponsor: box.sponsor?.name ?? '',
      deprecated: box.deprecated,

      priceWeight: box.priceWeight,
    }
  })

export const BoxesTable: React.FC<BoxesTableProps> = ({boxes, filter, setFilter, onClearFilter}) => {
  const [showExtraColumns, setShowExtraColumns] = useState(false)

  const hiddenColumnIds = useMemo(() => (showExtraColumns ? [] : EXTRA_COLUMN_IDS), [showExtraColumns])

  return (
    <Box my={2}>
      <Paper>
        <Box px={2} pt={2}>
          <BoxesPageFilter filter={filter} setFilter={setFilter} onClear={onClearFilter} rowCount={boxes.length} />
        </Box>

        <Box sx={{borderTop: '1px solid', borderColor: 'divider', mt: 2}} />

        <Box px={2} pt={2} display="flex" justifyContent="flex-end">
          <Button size="small" variant="outlined" onClick={() => setShowExtraColumns((v) => !v)} sx={{'&:hover': {backgroundColor: 'action.hover'}}}>
            {showExtraColumns ? 'Show less columns' : 'Show all columns'}
          </Button>
        </Box>

        <SortablePaginatedTable
          collapsible={false}
          minTableWidth={1050}
          maxVisibleWidth={1600}
          initialRowsPerPage={boxes.length}
          rowsPerPageOptions={[20, 50, 100, boxes.length]}
          initialOrderByKey={'name'}
          tableHeadCells={boxHeadCells}
          rows={createRows(boxes)}
          hiddenColumnIds={hiddenColumnIds}
          rowLinkTo={(row) => generatePath(Routes.BOX, {id: row.id})}
        />
      </Paper>
    </Box>
  )
}
